import { CampaignType_Enum as CampaignType } from '@weave/schema-gen-ts/dist/schemas/messaging/bulk/v2';
import { SortDirection } from '@weave/schema-gen-ts/dist/schemas/messaging/shared/v1/enums.pb';
import { useListCampaignDrafts } from '@frontend/api-bulk-messaging';
import { useHasBulkEmailAccess, useHasBulkTextAccess } from '@frontend/feature-access';
import { useTranslation } from '@frontend/i18n';
import { useAppScopeStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import { ContentLoader, Text } from '@frontend/design-system';
import { useHasAllotment } from '../../hooks/use-has-allotment';
import { BulkMessageError } from '../bulk-message-error';
import { Welcome } from '../campaign-list/campaign-list-body/welcome';
import { DraftsTable } from './drafts-table';

type Props = {
  selectedLocationIds: string[];
  selectedSort: SortDirection;
  selectedYear: number;
};

// TODO: Combine this with CampaignListBody, CampaignList, and DraftList since they are all now very similar
export const DraftsListBody = ({ selectedLocationIds, selectedSort, selectedYear }: Props) => {
  const { t } = useTranslation('bulk-messaging');

  const {
    hasAllotment: hasEmailAllotment,
    isError: isHasEmailAllotmentError,
    isLoading: isHasEmailAllotmentLoading,
  } = useHasAllotment({
    campaignType: CampaignType.EMAIL,
    selectedLocationIds,
  });

  const {
    hasAllotment: hasTextAllotment,
    isError: isHasTextAllotmentError,
    isLoading: isHasTextAllotmentLoading,
  } = useHasAllotment({
    campaignType: CampaignType.SMS,
    selectedLocationIds,
  });

  const hasAllotment = hasEmailAllotment || hasTextAllotment;

  const { selectedOrgId: orgId } = useAppScopeStore();
  const { hasBulkEmailAccess } = useHasBulkEmailAccess();
  const { hasBulkTextAccess } = useHasBulkTextAccess();

  const {
    data: drafts = [],
    isError: isDraftsError,
    isLoading: isDraftsLoading,
  } = useListCampaignDrafts(
    {
      locationIds: selectedLocationIds,
      orgId,
      sort: selectedSort,
      year: selectedYear,
    },
    hasBulkEmailAccess,
    hasBulkTextAccess
  );

  const isError = isDraftsError || isHasEmailAllotmentError || isHasTextAllotmentError;
  const isLoading = isDraftsLoading || isHasEmailAllotmentLoading || isHasTextAllotmentLoading;

  const hasSelectedLocations = selectedLocationIds.length > 0;
  const hasCampaigns = drafts.length > 0;

  if (isLoading) {
    return (
      <div css={{ position: 'relative', minHeight: '400px' }}>
        <ContentLoader show message={t('Loading Drafts...')} />
      </div>
    );
  }

  if (isError) {
    return <BulkMessageError heading={t('An unexpected error occurred while loading your drafts.')} />;
  }

  if (!hasSelectedLocations) {
    return (
      <Text size='large' css={{ marginTop: theme.spacing(15), textAlign: 'center' }}>
        {t('Select at least one location to view your drafts.')}
      </Text>
    );
  }

  if (!hasAllotment && !hasCampaigns) {
    return <Welcome />;
  }

  if (!hasCampaigns) {
    return (
      <Text size='large' css={{ marginTop: theme.spacing(15), textAlign: 'center' }}>
        {t('You do not have any drafts.')}
      </Text>
    );
  }

  return <DraftsTable drafts={drafts} hasAllotment={hasAllotment} />;
};
